import { initializeApp, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

import { firebaseConfig, storagePrefix, appCheckSiteKey, isFirebaseConfigured } from './firebase-config';

let app: FirebaseApp = null;

const getApp = (): FirebaseApp => {
    if (!app) {
        app = initializeApp(firebaseConfig);

        // App Check: attaches a token to every Storage request so the open `write`
        // rule on experiences/** can only be used by this app (no user login needed).
        if (appCheckSiteKey) {
            // on localhost, opt into a debug token so local testing still works
            // (the token is printed to the console to register in the console).
            if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
                (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
            }
            try {
                initializeAppCheck(app, {
                    provider: new ReCaptchaV3Provider(appCheckSiteKey),
                    isTokenAutoRefreshEnabled: true
                });
            } catch (err) {
                console.warn('App Check initialization failed', err);
            }
        }
    }
    return app;
};

type ProgressFn = (loaded: number, total: number) => void;

// turn a user-facing experience name into a safe storage filename component
const slugify = (name: string) => {
    const base = (name || 'experience')
    .toLowerCase()
    .replace(/\.ssproj$/i, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
    return base || 'experience';
};

// shared upload primitive: puts `data` at `storagePath` and resolves with its
// public download URL, reporting progress along the way.
const uploadAsset = async (data: Uint8Array | string, storagePath: string, contentType: string, cacheControl: string, onProgress?: ProgressFn): Promise<string> => {
    const storage = getStorage(getApp());
    const storageRef = ref(storage, storagePath);

    const blob = typeof data === 'string' ?
        new Blob([data], { type: contentType }) :
        new Blob([data as BlobPart], { type: contentType });
    const task = uploadBytesResumable(storageRef, blob, { contentType, cacheControl });

    return new Promise<string>((resolve, reject) => {
        task.on(
            'state_changed',
            snap => onProgress?.(snap.bytesTransferred, snap.totalBytes),
            err => reject(err),
            async () => {
                try {
                    resolve(await getDownloadURL(task.snapshot.ref));
                } catch (err) {
                    reject(err);
                }
            }
        );
    });
};

/**
 * Upload a serialized .ssproj to Firebase Storage and resolve with its public
 * download URL. The path is `experiences/<slug>.ssproj` (no timestamp) so
 * re-publishing a scene OVERWRITES the previous file instead of leaving orphans.
 * (Republishing regenerates the download token, so it yields a fresh share link.)
 */
const uploadScene = async (data: Uint8Array, name: string, onProgress?: ProgressFn): Promise<string> => {
    const slug = slugify(name);
    return uploadAsset(data, `${storagePrefix}/${slug}.ssproj`, 'application/octet-stream', 'public, max-age=300', onProgress);
};

/**
 * Upload a Studio-bound asset (compressed .ply splat data or a settings.json)
 * under `studio-scenes/<sceneId>/<filename>`, overwriting on republish.
 */
const uploadStudioAsset = async (data: Uint8Array | string, sceneId: string, filename: string, contentType: string, onProgress?: ProgressFn): Promise<string> => {
    return uploadAsset(data, `studio-scenes/${sceneId}/${filename}`, contentType, 'public, max-age=300', onProgress);
};

export { uploadScene, uploadStudioAsset, slugify, isFirebaseConfigured };
